import ContractRefundStatus from 'app/components/ContractRefundStatus';
import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import ThemePageError from 'assets/components/page/themes/Theme.Page.Error';
import ThemePageList from 'assets/components/page/themes/Theme.Page.List';
import Pagination from 'assets/components/pagination/Pagination';
import ThemePaginationRounded from 'assets/components/pagination/themes/Theme.Pagination.Rounded';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';
import { defaultDateFormat, localizeDate } from 'assets/utils/data/Date';
import { roundToStep } from 'assets/utils/data/Number';
import { isEmpty, merge } from 'lodash';
import ContractRefundStatusCode from 'models/enums/ContractRefundStatusCode';
import React from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import { ContractRefundAction, useContractRefundProvider } from '../Sales.ContractRefund.Hooks';
import SalesContractRefundFilters from './Sales.ContractRefund.Filters';

export default function SalesContractRefundList() {
  const { lang } = getLocales();
  const { listApi, pageRouter, permissions } = useContractRefundProvider();

  return !listApi.payload && !listApi.isExecuting && listApi.apiCounter > 0 ? (
    <Page
      class={ThemePageError}
      sidebar={<Icon class="fas-clipboard-list" />}
      header={lang.refundQuotes}
      render={lang.noInfo}
    />
  ) : (
    <Page
      class={ThemePageList}
      isLoading={listApi.isExecuting}
      header={
        <>
          <h2 style={{ marginRight: 'auto' }} children={lang.refundQuotes} />
          {isMobile && <SalesContractRefundFilters />}
        </>
      }
      subHeader={isDesktop && <SalesContractRefundFilters />}
      render={
        <table className={ThemeTableSimple}>
          <thead>
            <tr>
              <th>{lang.status}</th>
              <th>{lang.jobId}</th>
              <th>{lang.appNumber}</th>
              <th>{lang.initiatedBy}</th>
              <th>{lang.dateInitiated}</th>
              <th>{lang.cancellationDate}</th>
              <th>{lang.contractExpiration}</th>
              <th>{lang.loyaltyPremium}</th>
              <th>{lang.hasClaims}</th>
              <th>{lang.retailSalePrice}</th>
              <th>{lang.refundAmount}</th>
              <th data-actions>{lang.actions}</th>
            </tr>
          </thead>
          <tbody>
            <DataMap
              data={listApi.payload?.data}
              render={({ data: contractRefund }) => {
                const contract = contractRefund?.contractCancellation?.contract;

                return (
                  <React.Fragment key={contractRefund?.id}>
                    <tr style={{ borderTop: `2px solid ${Theme.Color.shadow}` }}>
                      <td>
                        <ContractRefundStatus
                          statusCode={contractRefund?.statusCode as Model.Enum.ContractRefundStatusCode}
                        />
                      </td>
                      <td>
                        <b>{contractRefund?.jobId}</b>
                      </td>
                      <td>{contract?.appNumber}</td>
                      <td>
                        <span>
                          <strong>{lang.user}:</strong> {contractRefund?.createdBy?.fullName}
                        </span>
                        <br />
                        {contractRefund?.dealer && (
                          <span>
                            <strong>{lang.api.toLocaleUpperCase()}:</strong> {contractRefund?.dealer?.name}
                          </span>
                        )}
                      </td>
                      <td>{localizeDate(contractRefund?.createdAtUtc, defaultDateFormat)}</td>
                      <td>
                        {localizeDate(contractRefund?.contractCancellation?.cancellationDateLocal, defaultDateFormat)}
                      </td>
                      <td>{localizeDate(contract?.expiryDate, defaultDateFormat)}</td>
                      <td>{contractRefund?.isLoyaltyPremiumApplied ? lang.yes : lang.no}</td>
                      <td>{contract?.claimCount > 0 ? lang.yes : lang.no}</td>
                      <td>
                        <strong>
                          <FormatValue value={`$${roundToStep(contractRefund?.retailSalePriceValueAtSale, 0.01)}`} />
                        </strong>
                      </td>
                      <td>
                        <strong>
                          <FormatValue value={`$${contractRefund?.totalToBeRefunded}`} />
                        </strong>
                      </td>
                      <td>
                        <Box class={ThemeWrapperStack}>
                          <Button
                            class={ThemeButtonCircle}
                            media={<Icon class="fas-info-circle" />}
                            onClick={pageRouter.updateParams}
                            data={{ id: contractRefund.id.toString(), action: ContractRefundAction.INFO }}
                            htmlElementProps={{
                              title: lang.info,
                            }}
                            active
                          />
                          <Button
                            class={ThemeButtonCircle}
                            media={<Icon class="fas-credit-card" />}
                            onClick={pageRouter.updateParams}
                            data={{ id: contractRefund.id.toString(), action: ContractRefundAction.PROCESS }}
                            htmlElementProps={{
                              title: lang.processRefund,
                            }}
                            disabled={
                              !permissions?.EDIT_CONTRACTREFUND ||
                              contractRefund.statusCode !== ContractRefundStatusCode.OPEN
                            }
                            active
                          />
                          <Button
                            class={ThemeButtonCircle}
                            media={<Icon class="fas-sliders-h" />}
                            onClick={pageRouter.updateParams}
                            data={{
                              id: contractRefund.id.toString(),
                              action: ContractRefundAction.DEALER_PAYMENT_CONTROLS,
                            }}
                            htmlElementProps={{
                              title: lang.dealerPaymentControls,
                            }}
                            disabled={
                              !(
                                permissions?.EDIT_CONTRACTREFUND &&
                                contractRefund.statusCode === ContractRefundStatusCode.PROCESSING &&
                                isEmpty(contractRefund.dealerPaymentControls)
                              )
                            }
                            active
                          />
                          <Button
                            class={ThemeButtonCircle}
                            media={<Icon class="fas-ban" />}
                            onClick={pageRouter.updateParams}
                            data={{ id: contractRefund.id.toString(), action: ContractRefundAction.CANCEL }}
                            htmlElementProps={{
                              title: lang.cancelRefund,
                            }}
                            disabled={
                              !(
                                permissions?.CANCEL_CONTRACTREFUND &&
                                (contractRefund.statusCode === ContractRefundStatusCode.OPEN ||
                                  contractRefund.statusCode === ContractRefundStatusCode.PROCESSING) &&
                                isEmpty(contractRefund.dealerPaymentControls)
                              )
                            }
                            active
                          />
                        </Box>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              }}
            />
          </tbody>
        </table>
      }
      footer={
        <Pagination
          class={ThemePaginationRounded}
          count={listApi.payload?.count}
          offset={listApi.body?.offset}
          limit={listApi.body?.limit}
          onLimitChange={(limit) =>
            pageRouter.updateQuery(
              merge(pageRouter.query, {
                list: {
                  limit,
                  offset: 0,
                },
              })
            )
          }
          onOffsetChange={(offset) =>
            pageRouter.updateQuery(
              merge(pageRouter.query, {
                list: {
                  offset,
                },
              })
            )
          }
        />
      }
    />
  );
}
